import React, { useState, useMemo } from 'react';

const PRESETS = [
  { key: 'all', label: 'All time' },
  { key: 'week', label: 'Last week' },
  { key: 'month', label: 'Last month' },
  { key: '3months', label: 'Last 3 months' },
  { key: 'custom', label: 'Custom' },
];

function getPresetRange(presetKey) {
  if (presetKey === 'all') return { dateFrom: null, dateTo: null };

  const now = Math.floor(Date.now() / 1000);
  const DAY = 86400;

  switch (presetKey) {
    case 'week':
      return { dateFrom: now - 7 * DAY, dateTo: now };
    case 'month':
      return { dateFrom: now - 30 * DAY, dateTo: now };
    case '3months':
      return { dateFrom: now - 90 * DAY, dateTo: now };
    default:
      return { dateFrom: null, dateTo: null };
  }
}

function inputValueToUnix(dateString) {
  if (!dateString) return null;
  const d = new Date(dateString + 'T00:00:00');
  return Math.floor(d.getTime() / 1000);
}

function DateFilter({ value, onChange, colors }) {
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const preset = value?.preset || 'all';

  const effectiveColors = useMemo(() => ({
    text: '#1a1a1a',
    textMuted: '#888',
    pillBg: '#f5f5f5',
    pillBorder: '#e5e5e5',
    accent: '#111',
    border: '#eee',
    inputBg: '#fafafa',
    ...colors,
  }), [colors]);

  const handlePresetClick = (key) => {
    if (key === 'custom') {
      onChange({ preset: 'custom', dateFrom: inputValueToUnix(customFrom), dateTo: inputValueToUnix(customTo) });
    } else {
      const range = getPresetRange(key);
      onChange({ preset: key, ...range });
    }
  };

  const handleCustomFromChange = (e) => {
    const val = e.target.value;
    setCustomFrom(val);
    if (preset === 'custom') {
      onChange({ preset: 'custom', dateFrom: inputValueToUnix(val), dateTo: inputValueToUnix(customTo) });
    }
  };

  const handleCustomToChange = (e) => {
    const val = e.target.value;
    setCustomTo(val);
    if (preset === 'custom') {
      onChange({ preset: 'custom', dateFrom: inputValueToUnix(customFrom), dateTo: inputValueToUnix(val) });
    }
  };

  const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {PRESETS.map(({ key, label }) => {
          const active = preset === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handlePresetClick(key)}
              style={{
                padding: '5px 14px',
                fontSize: '0.8rem',
                fontFamily: fontStack,
                fontWeight: active ? 600 : 400,
                backgroundColor: active ? effectiveColors.accent : effectiveColors.pillBg,
                color: active ? '#fff' : effectiveColors.text,
                border: `1px solid ${active ? effectiveColors.accent : effectiveColors.pillBorder}`,
                borderRadius: '999px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                outline: 'none',
                lineHeight: 1.4,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {preset === 'custom' && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <label style={{ fontSize: '0.8rem', color: effectiveColors.textMuted, fontFamily: fontStack }}>
            From
          </label>
          <input
            type="date"
            value={customFrom}
            onChange={handleCustomFromChange}
            style={{
              padding: '5px 10px',
              fontSize: '0.8rem',
              fontFamily: fontStack,
              borderRadius: '8px',
              border: `1px solid ${effectiveColors.border}`,
              background: effectiveColors.inputBg,
              color: effectiveColors.text,
              outline: 'none',
            }}
          />
          <label style={{ fontSize: '0.8rem', color: effectiveColors.textMuted, fontFamily: fontStack }}>
            To
          </label>
          <input
            type="date"
            value={customTo}
            onChange={handleCustomToChange}
            style={{
              padding: '5px 10px',
              fontSize: '0.8rem',
              fontFamily: fontStack,
              borderRadius: '8px',
              border: `1px solid ${effectiveColors.border}`,
              background: effectiveColors.inputBg,
              color: effectiveColors.text,
              outline: 'none',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DateFilter;
